import React from 'react';
import { Link } from 'react-router-dom';
import CachedImage from './CachedImage';
import {
  LayoutDashboard,
  Package,
  Users,
  BarChart3,
  Settings,
  Image,
  ShoppingCart,
  FileText,
  Globe,
  Database,
  Palette,
  Shield,
  Home,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Brain,
  Zap
} from 'lucide-react';

const AdminSidebar = ({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen }) => {
  const menuItems = [
    {
      id: 'overview',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Overview & Analytics'
    },
    {
      id: 'products',
      label: 'Products',
      icon: Package,
      description: 'Manage Products'
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: ShoppingCart,
      description: 'Order Management'
    },
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      description: 'User Management'
    },
    {
      id: 'chat',
      label: 'Chat Support',
      icon: MessageCircle,
      description: 'AI Chat & Support'
    },
    {
      id: 'ai-training',
      label: 'AI Training',
      icon: Brain,
      description: 'Train AI Assistant'
    },
    {
      id: 'auto-training-demo',
      label: 'Auto Training Demo',
      icon: Zap,
      description: 'Test Auto Training'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      description: 'Sales & Reports'
    },
    {
      id: 'images',
      label: 'Images',
      icon: Image,
      description: 'Image Manager'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'Site Configuration'
    }
  ];

  const quickActions = [
    {
      id: 'view-site',
      label: 'View Site',
      icon: Globe,
      href: '/',
      external: true
    },
    {
      id: 'database',
      label: 'Database',
      icon: Database,
      href: '/database-test'
    },
    {
      id: 'firebase',
      label: 'Firebase',
      icon: Shield,
      href: '/firebase-test'
    }
  ];

  return (
    <div className={`fixed left-0 top-0 h-full bg-white/95 backdrop-blur-sm shadow-xl border-r border-gray-200/50 transition-all duration-300 z-30 ${
      sidebarOpen ? 'w-64' : 'w-16'
    }`}>
      {/* Logo Section */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {sidebarOpen ? (
          <Link to="/" className="flex items-center space-x-3">
            <CachedImage
              src="/images/logo/logo.jpg"
              alt="Gifted Solutions"
              className="h-8 w-auto rounded"
              loading="eager"
            />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Admin Panel</h2>
              <p className="text-xs text-gray-500">Gifted Solutions</p>
            </div>
          </Link>
        ) : (
          <div className="flex justify-center">
            <CachedImage
              src="/images/logo/logo.jpg"
              alt="Gifted Solutions"
              className="h-8 w-8 rounded"
              loading="eager"
            />
          </div>
        )}
        
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-1 rounded hover:bg-gray-100 hidden lg:block"
        >
          {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-3 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center px-3 py-3 rounded-xl text-left transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900 hover:shadow-sm'
                }`}
                title={!sidebarOpen ? item.label : ''}
              >
                <Icon size={20} className={`${isActive ? 'text-white' : 'text-gray-500'} ${
                  sidebarOpen ? 'mr-3' : 'mx-auto'
                }`} />
                {sidebarOpen && (
                  <div>
                    <div className="font-medium">{item.label}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Quick Actions */}
        {sidebarOpen && (
          <div className="mt-8 px-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Quick Actions
            </h3>
            <div className="space-y-1">
              {quickActions.map((action) => {
                const Icon = action.icon;
                
                if (action.external) {
                  return (
                    <a
                      key={action.id}
                      href={action.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50 hover:text-gray-900"
                    >
                      <Icon size={16} className="mr-3 text-gray-400" />
                      {action.label}
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={action.id}
                    to={action.href}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-50 hover:text-gray-900"
                  >
                    <Icon size={16} className="mr-3 text-gray-400" />
                    {action.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Footer */}
      {sidebarOpen && (
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            <p>Gifted Solutions Admin</p>
            <p>v1.0.0</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSidebar;
